import { initializeLocale, setLocale } from '@/i18n'
import { useSettingsStore } from '@/stores/settingsStore'
import { createRouter, createWebHistory } from 'vue-router'
import { AuthService } from '../services/authService'
import { ROUTE_MASTERDATA_PARTNERS, ROUTE_MOVEMENT_DATA_CATSCREDITS, ROUTE_MASTERDATA_FEEDSTOCK, ROUTE_MOVEMENT_DATA_INBOUNDS, ROUTE_MOVEMENT_DATA_MASSBALANCES, ROUTE_MOVEMENT_DATA_OUTBOUNDS, ROUTE_MOVEMENT_DATA_SUSTAINABILITYDATA, TENANT_CONFIGS, ROUTE_MASTERDATA_LOCATIONS, ROUTE_MASTERDATA_PRODUCTS, ROUTE_MASTERDATA_COUNTRY_CODES, ROUTE_MASTERDATA_PROVINCE_CODES, ROUTE_IMPORT_MESSAGES, ROUTE_REPORTS_ONSITE_REPORT_FACILITY_INPUT, ROUTE_REPORTS_ONSITE_REPORT_FACILITY_OUTPUT, ROUTE_REPORTS_OFFSITE_REPORT_FACILITY_INPUT, ROUTE_REPORTS_PRODUCT_MATERIAL_BALANCE, ROUTE_MOVEMENT_DATA_CATSCREDITS_HISTORY, ROUTE_REPORTS_CREDIT_GENERATION, ROUTE_MOVEMENT_DATA_CARBON_INTENSITY_CONFIGURATIONS, PROTECTED_ROUTES, ROUTE_MASTERDATA_PRODUCT_GROUP_ENERGY_DENSITIES, ROUTE_MASTERDATA_PRODUCT_GROUPS, ROUTE_MOVEMENT_DATA_CONTRACTS, ROUTE_MOVEMENT_DATA_CONTRACT_POSITIONS, ROUTE_REPORTS_BIOFUEL_PURCHASES, ROUTE_REPORTS_ANNUAL_CREDIT_ADJUSTMENT, ROUTE_REPORTS_STANDALONE_CREDIT_SUMMARY, ROUTE_CFR_CREDIT_RECONCILATION_SUMMARY_REPORT, ROUTE_CFR_BIO_IMPORT_CREDIT_REPORT } from '@/constants/tenants'
import { useUserStore } from '@/stores/user'
import { CheckAuthService } from '@/services/checkAuthService'

const DEFAULT_LANG = 'en'

function getPreferredLanguage(): string {
  return DEFAULT_LANG  // Immer 'de' zurückgeben
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // Root-Route mit intelligenter Sprachweiterleitung
    {
      path: '/',
      redirect: () => `/${getPreferredLanguage()}`,
      meta: { requiresAuth: false, isPublic: true }
    },
    // Login-Route mit Sprachweiterleitung
    {
      path: '/login',
      redirect: () => `/${getPreferredLanguage()}/login`,
      meta: { requiresAuth: false, isPublic: true }
    },
    {
      path: '/:lang',
      children: [
        // Login / Home
        {
          path: 'login',
          name: 'login-lang',
          component: () => import('../views/LoginView.vue'),
          meta: { requiresAuth: false, isPublic: true }
        },
        {
          path: '',
          name: 'home-lang',
          component: () => import('../views/HomeView.vue'),
          meta: { requiresAuth: false, isPublic: true }
        },

        // movement data
        {
          path: 'movement-data',
          children: [
            {
              path: 'inbounds',
              name: ROUTE_MOVEMENT_DATA_INBOUNDS,
              component: () => import('../views/movement-data/InboundsView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'inbounds/:number/edit',
              name: `${ROUTE_MOVEMENT_DATA_INBOUNDS}-edit`,
              component: () => import('../views/movement-data/InboundsEditView.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              path: 'outbounds',
              name: ROUTE_MOVEMENT_DATA_OUTBOUNDS,
              component: () => import('../views/movement-data/OutboundsView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'outbounds/:number/edit',
              name: `${ROUTE_MOVEMENT_DATA_OUTBOUNDS}-edit`,
              component: () => import('../views/movement-data/OutboundsEditView.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              path: 'sustainabilitydata',
              name: ROUTE_MOVEMENT_DATA_SUSTAINABILITYDATA,
              component: () => import('../views/movement-data/SustainabilityDataView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'sustainabilitydata/:number/edit',
              name: `${ROUTE_MOVEMENT_DATA_SUSTAINABILITYDATA}-edit`,
              component: () => import('../views/movement-data/SustainabilityDataEditView.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              path: 'mass-balances',
              name: ROUTE_MOVEMENT_DATA_MASSBALANCES,
              component: () => import('../views/movement-data/MassBalancesView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'mass-balances/create',
              name: `${ROUTE_MOVEMENT_DATA_MASSBALANCES}-create`,
              component: () => import('../views/movement-data/MassBalanceEditView.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              path: 'mass-balances/:identifierValue/edit',
              name: `${ROUTE_MOVEMENT_DATA_MASSBALANCES}-edit`,
              component: () => import('../views/movement-data/MassBalanceEditView.vue'),
              props: true,
              meta: { requiresAuth: true },
            },
            {
              path: 'catscredits',
              name: ROUTE_MOVEMENT_DATA_CATSCREDITS,
              component: () => import('../views/movement-data/CatsCreditView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'catscredits/:id/edit',
              name: `${ROUTE_MOVEMENT_DATA_CATSCREDITS}-edit`,
              component: () => import('../views/movement-data/CatsCreditEditView.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              path: 'catscreditshistory',
              name: `${ROUTE_MOVEMENT_DATA_CATSCREDITS_HISTORY}`,
              component: () => import('../views/movement-data/CatsCreditHistoryView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'catscreditshistory/:id/edit',
              name: `${ROUTE_MOVEMENT_DATA_CATSCREDITS_HISTORY}-edit`,
              component: () => import('../views/movement-data/CatsCreditHistoryEditView.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              path: 'contracts',
              name: ROUTE_MOVEMENT_DATA_CONTRACTS,
              component: () => import('../views/masterdata/Contracts.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'contracts/:number/edit',
              name: `${ROUTE_MOVEMENT_DATA_CONTRACTS}-edit`,
              component: () => import('../views/movement-data/ContractsEditView.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              path: 'contract-positions/:number/edit',
              name: `${ROUTE_MOVEMENT_DATA_CONTRACT_POSITIONS}-edit`,
              component: () => import('../views/movement-data/ContractPositionsEditView.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
          ]
        },

        // Stammdaten
        {
          path: 'masterdata',
          children: [
            {
              path: 'partners',
              name: ROUTE_MASTERDATA_PARTNERS,
              component: () => import('../views/masterdata/PartnerView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'partners/:number/edit',
              name: `${ROUTE_MASTERDATA_PARTNERS}-edit`,
              component: () => import('../views/masterdata/PartnerEditView.vue'),
              meta: { requiresAuth: true },
              props: true,
            },
            {
              path: 'feedstock',
              name: ROUTE_MASTERDATA_FEEDSTOCK,
              component: () => import('../views/masterdata/FeedstockView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'feedstock/:id',
              name: `${ROUTE_MASTERDATA_FEEDSTOCK}-edit`,
              component: () => import('../views/masterdata/FeedstockEditView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'locations',
              name: ROUTE_MASTERDATA_LOCATIONS,
              component: () => import('../views/masterdata/LocationsView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'locations/:number/edit',
              name: `${ROUTE_MASTERDATA_LOCATIONS}-edit`,
              component: () => import('../views/masterdata/LocationEditView.vue'),
              meta: { requiresAuth: true },
              props: true,
            },
            {
              path: 'products',
              name: ROUTE_MASTERDATA_PRODUCTS,
              component: () => import('../views/masterdata/ProductsView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'products/:number/edit',
              name: `${ROUTE_MASTERDATA_PRODUCTS}-edit`,
              component: () => import('../views/masterdata/ProductEditView.vue'),
              meta: { requiresAuth: true },
              props: true,
            },
            {
              path: 'product-groups',
              name: ROUTE_MASTERDATA_PRODUCT_GROUPS,
              component: () => import('../views/masterdata/ProductGroupsView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'product-groups/:group/edit',
              name: `${ROUTE_MASTERDATA_PRODUCT_GROUPS}-edit`,
              component: () => import('../views/masterdata/ProductGroupEditView.vue'),
              meta: { requiresAuth: true },
              props: true,
            },
            {
              path: 'product-group-energy-densities',
              name: ROUTE_MASTERDATA_PRODUCT_GROUP_ENERGY_DENSITIES,
              component: () => import('../views/masterdata/ProductGroupEnergyDensitiesView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'country-codes',
              name: ROUTE_MASTERDATA_COUNTRY_CODES,
              component: () => import('../views/masterdata/CountryCodesView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'province-codes',
              name: ROUTE_MASTERDATA_PROVINCE_CODES,
              component: () => import('../views/masterdata/ProvinceCodesView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'carbonintensityconfigurations/:id/edit',
              name: `${ROUTE_MOVEMENT_DATA_CARBON_INTENSITY_CONFIGURATIONS}-edit`,
              component: () => import('../views/masterdata/CarbonIntensityConfigurationEditView.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
          ]
        },

        // Imports
        {
          path: 'import-messages',
          name: ROUTE_IMPORT_MESSAGES,
          component: () => import('../views/import/ImperialoilImportView.vue'),
          meta: { requiresAuth: true },
        },

        // Reports
        {
          path: 'reports',
          children: [
            {
              path: 'onsite-report-facility-input',
              name: ROUTE_REPORTS_ONSITE_REPORT_FACILITY_INPUT,
              component: () => import('../views/reports/OnSiteReportFacilityInput.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'onsite-report-facility-output',
              name: ROUTE_REPORTS_ONSITE_REPORT_FACILITY_OUTPUT,
              component: () => import('../views/reports/OnSiteReportFacilityOutput.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'offsite-report-facility-input',
              name: ROUTE_REPORTS_OFFSITE_REPORT_FACILITY_INPUT,
              component: () => import('../views/reports/OffSiteReportFacilityInput.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'product-material-balance-report',
              name: ROUTE_REPORTS_PRODUCT_MATERIAL_BALANCE,
              component: () => import('../views/reports/ProductionMaterialBalanceReportView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'credit-generation-report',
              name: ROUTE_REPORTS_CREDIT_GENERATION,
              component: () => import('../views/reports/CreditGenerationReportView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'biofuel-purchases-report',
              name: ROUTE_REPORTS_BIOFUEL_PURCHASES,
              component: () => import('../views/reports/BioFuelPurchasesReport.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'annual-credit-adjustment',
              name: ROUTE_REPORTS_ANNUAL_CREDIT_ADJUSTMENT,
              component: () => import('../views/reports/AnualCreditAdjustmentReport.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'standalone-agreement-credit-summary',
              name: ROUTE_REPORTS_STANDALONE_CREDIT_SUMMARY,
              component: () => import('../views/reports/StandaloneCreditOverview.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: ROUTE_CFR_CREDIT_RECONCILATION_SUMMARY_REPORT,
              name: ROUTE_CFR_CREDIT_RECONCILATION_SUMMARY_REPORT,
              component: () => import('../views/reports/CfrCreditReconcilationSummaryReport.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: ROUTE_CFR_BIO_IMPORT_CREDIT_REPORT,
              name: ROUTE_CFR_BIO_IMPORT_CREDIT_REPORT,
              component: () => import('../views/reports/CfrBioImportCreditReport.vue'),
              meta: { requiresAuth: true }
            },
          ]
        },

        // Hilfe
        {
          path: 'hilfe',
          children: [
            {
              path: 'dokumentation',
              name: 'dokumentation',
              component: () => import('../views/hilfe/DokumentationView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'faq',
              name: 'faq',
              component: () => import('../views/hilfe/FAQView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'hilfevideos',
              name: 'hilfevideos',
              component: () => import('../views/hilfe/HilfevideosView.vue'),
              meta: { requiresAuth: true }
            }
          ]
        },

        // Benutzer
        {
          path: 'user',
          children: [
            {
              path: 'dashboard',
              name: 'dashboard',
              component: () => import('../views/benutzer/DashboardView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'passwort',
              name: 'passwort-wechseln',
              component: () => import('../views/benutzer/PasswortWechselnView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: '2fa-reset',
              name: '2fa-reset',
              component: () => import('../views/benutzer/TwoFactorResetView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'einstellungen',
              name: 'einstellungen',
              component: () => import('../views/benutzer/EinstellungenView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'nachrichten',
              name: 'nachrichten',
              component: () => import('../views/benutzer/NachrichtenView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'administration',
              name: 'user-administration',
              component: () => import('../views/benutzer/UserAdministrationView.vue'),
              meta: { requiresAuth: true, requiresAdmin: true }
            },
            {
              path: 'mandanten',
              name: 'mandanten-verwaltung',
              component: () => import('../views/benutzer/MandantenView.vue'),
              meta: { requiresAuth: true, requiresAdmin: true }
            },
            {
              path: 'rollen',
              name: 'rollen-verwaltung',
              component: () => import('../views/benutzer/RollenView.vue'),
              meta: { requiresAuth: true, requiresAdmin: true }
            },
            {
              path: 'release-notes',
              name: 'release-notes',
              component: () => import('../views/benutzer/ReleaseNotesView.vue'),
              meta: { requiresAuth: true }
            }
          ]
        },

        // Layouts (angepasste Pfade, da AboutView & Co. im root von views liegen)
        {
          path: 'layouts/about',
          component: () => import('../views/AboutView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/accordion',
          component: () => import('../views/AccordionView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/badge',
          component: () => import('../views/BadgeView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/breadcrumb',
          component: () => import('../views/BreadcrumbView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/button-group',
          component: () => import('../views/ButtonGroupView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/button-icons',
          component: () => import('../views/ButtonIconsView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/button',
          component: () => import('../views/ButtonView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/data-display',
          component: () => import('../views/DataDisplayView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/date-picker',
          component: () => import('../views/DatePickerView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/input',
          component: () => import('../views/InputView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/pagination',
          component: () => import('../views/PaginationView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/sidebar',
          component: () => import('../views/SidebarView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/table',
          component: () => import('../views/TableView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'layouts/tabs-group',
          component: () => import('../views/TabsGroupView.vue'),
          meta: { requiresAuth: true }
        },

        // 404-Route für nicht gefundene Seiten innerhalb der Sprach-Route
        {
          path: ':pathMatch(.*)*',
          name: 'not-found-lang',
          component: () => import('../views/NotFoundView.vue'),
          meta: { requiresAuth: false, isPublic: true }
        }
      ]
    },
    // Globale 404-Route
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFoundView.vue'),
      meta: { requiresAuth: false, isPublic: true }
    }
  ]
})

// Navigation Guard
router.beforeEach(async (to, from, next) => {
  const authService = new AuthService();
  const checkAuthService = new CheckAuthService();
  const isAuthenticated = !!(await checkAuthService.isAuthenticated())
  const settingsStore = useSettingsStore()
  const userStore = useUserStore();

  await userStore.init();

  // Prüfe ob die Session noch gültig ist
  // if (isAuthenticated) {
  //   const remainingTime = authService.getRemainingTime()
  //   if (remainingTime <= 0) {
  //     await authService.logout()
  //     next({
  //       name: 'login-lang',
  //       query: { redirect: to.fullPath },
  //       params: { lang: to.params.lang || DEFAULT_LANG }
  //     })
  //     return
  //   }
  // }

  // Handle 404 errors
  if (to.name === 'not-found' || to.name === 'not-found-lang') {
    next()
    return
  }

  // Check language in URL and set it
  const urlLang = to.params.lang as string
  const path = to.path

  if (urlLang) {
    // Nur 'de' und 'en' sind erlaubt
    if (urlLang !== 'de' && urlLang !== 'en') {
      next('/de' + path.substring(3)) // Ersetze ungültige Sprache durch 'de'
      return
    }

    // Setze die Sprache, wenn sie sich geändert hat
    if (urlLang !== settingsStore.settings.language) {
      await settingsStore.saveSettings({
        ...settingsStore.settings,
        language: urlLang
      })
      setLocale(urlLang)
      localStorage.setItem('locale', urlLang)
    }
  } else if (!path.startsWith('/de/') && !path.startsWith('/en/')) {
    // Wenn keine Sprache in der URL ist, leite zu /de/ weiter
    const newPath = path === '/' ? '/de' : '/de' + path
    next(newPath)
    return
  }

  // Prüfe Berechtigungen für geschützte Routen
  if (to.meta.requiresAuth) {
    if (!isAuthenticated) {
      // Nicht eingeloggt - Weiterleitung zum Login
      const redirectPath = to.fullPath
      next({
        name: 'login-lang',
        query: { redirect: redirectPath },
        params: { lang: urlLang || DEFAULT_LANG }
      })
      return
    }

    // Prüfe Admin-Berechtigung
    if (to.meta.requiresAdmin && !authService.isAdmin()) {
      next({ name: 'dashboard', params: { lang: urlLang || DEFAULT_LANG } })
      return
    }

    // Prüfe Support-Berechtigung
    if (to.meta.requiresSupport && !authService.getCurrentUser()?.istSupport) {
      next({ name: 'dashboard', params: { lang: urlLang || DEFAULT_LANG } })
      return
    }

    // Prüfe Mandanten-Zuordnung
    const currentMandant = userStore.tenant;
    if (!currentMandant) {
      next({ name: 'mandant-selection', params: { lang: urlLang || DEFAULT_LANG } })
      return
    }

    // const testName = to.name?.toString().replace('-edit', '') || '';
    // const tenantConfig = TENANT_CONFIGS[userStore.tenant]
    // if (PROTECTED_ROUTES.includes(testName) && !tenantConfig.visibleRoutes.includes(testName)) {
    //   next({ name: 'dashboard', params: { lang: urlLang || DEFAULT_LANG } })
    // }
  }

  // Verhindere Zugriff auf Login-Seite wenn bereits eingeloggt
  if (isAuthenticated && to.name === 'login-lang') {
    next({ name: 'dashboard', params: { lang: urlLang || DEFAULT_LANG } })
    return
  }

  // Erlaube Zugriff auf öffentliche Routen
  if (to.meta.isPublic) {
    next()
    return
  }

  // if (!tenantConfig?.visibleRoutes?.includes(String(to.name))) {
  //   next({ name: 'dashboard', params: { lang: urlLang || DEFAULT_LANG } });
  //   return;
  // }
  next()
})

export default router
